import React, { useState } from 'react';
import axios from 'axios';
import { Formik } from 'formik';
import { cloneDeep, isEmpty } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import Text from '~/components/core/TextComponents/Text';
import FieldConfigurationCard from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/FieldConfigurationCard/FieldConfigurationCard';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError } from '~/Utils';

interface PhysicalMailTypeOption {
  id: string;
  desc: string;
  active: boolean;
}

interface PhysicalMailTypeField {
  type: string;
  id: string;
  desc: string;
  active: boolean;
  mandatory: boolean;
  options: PhysicalMailTypeOption[];
}

interface PhysicalMailTypesConfig {
  physical_mail_type: PhysicalMailTypeField;
}

const DEFAULT_CONFIG: PhysicalMailTypesConfig = {
  physical_mail_type: {
    type: 'select',
    id: 'physical_mail_type',
    desc: 'Mail Type',
    active: true,
    mandatory: false,
    options: [],
  },
};

const PhysicalMailConfigurationTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: { id: number } };
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const { data, isLoading, isError, reloadData } = useDataFetcher<PhysicalMailTypesConfig>(
    `/sysconfig/api/v1/organizations/${organization.id}/physical_mail_configuration`,
    null,
    { shouldFetch: !!organization?.id }
  );

  const currentConfig: PhysicalMailTypesConfig = !isEmpty(data) && data ? data : DEFAULT_CONFIG;
  const pmtField = currentConfig.physical_mail_type;
  const activeOptions = pmtField?.options?.filter((opt) => opt.active) ?? [];

  const handleSubmit = async (values: PhysicalMailTypesConfig) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/physical_mail_configuration`, values);
      await reloadData();
      setIsEditDialogOpen(false);
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <div className="bg-slate-100 pt-32">
        <CardDialog title="Physical Mail Types" width="md">
          <div className="space-y-16">
            <div className="flex items-center justify-between">
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Status: {pmtField?.active ? 'Enabled' : 'Disabled'}
                  {pmtField?.active && pmtField?.mandatory ? ' (Required)' : ''}
                </Text>
              </div>
              <Button variant="contained" color="primary" onClick={() => setIsEditDialogOpen(true)}>
                Edit Configuration
              </Button>
            </div>

            {activeOptions.length > 0 ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="mb-8">
                  Configured Types
                </Text>
                <div className="flex flex-wrap gap-8">
                  {activeOptions.map((option) => (
                    <Chip key={option.id} label={option.desc} size="small" variant="outlined" />
                  ))}
                </div>
              </div>
            ) : (
              <Text
                variant={Text.VARIANTS.SM}
                weight={Text.WEIGHTS.REGULAR}
                colorVariant={Text.COLOR_VARIANTS.DISABLED}
              >
                No mail types configured. Click Edit Configuration to add types.
              </Text>
            )}
          </div>
        </CardDialog>
      </div>

      {isEditDialogOpen && (
        <Formik
          initialValues={cloneDeep(currentConfig)}
          validationSchema={Yup.object().shape({
            physical_mail_type: Yup.object().shape({
              desc: Yup.string().required('Required'),
              options: Yup.array().of(
                Yup.object().shape({
                  id: Yup.string().required('Required'),
                  desc: Yup.string().required('Required'),
                })
              ),
            }),
          })}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ handleSubmit: formikHandleSubmit, isSubmitting }) => (
            <CardDialog
              isDialog
              maxWidth="md"
              fullWidth
              onClose={() => setIsEditDialogOpen(false)}
              title="Edit Physical Mail Configuration"
              preventClose={isSubmitting}
              footerActions={
                <DialogFooterActions
                  disabled={isSubmitting}
                  onClickPrimary={() => formikHandleSubmit()}
                  onClickSecondary={() => setIsEditDialogOpen(false)}
                  primaryLabel="Save"
                  secondaryLabel="Cancel"
                />
              }
            >
              <div className="space-y-6">
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Configure the mail type field that will appear in the physical mail form.
                </Text>

                <div className="w-full">
                  <FieldConfigurationCard
                    fieldId="physical_mail_type"
                    isConfiguredField={false}
                    defaultExpanded
                    forceDisabled={isSubmitting}
                    disableCategories
                    disableType
                    disableCliveAdditionalInstructions
                    withoutRequiredForClaimClosing
                    requiredLabel="Required field for physical mail submission"
                    withRequiredConfirmationModal={false}
                  />
                </div>
              </div>
            </CardDialog>
          )}
        </Formik>
      )}
    </LoadingSwitch>
  );
};

export default PhysicalMailConfigurationTab;
